/**
 * 조직관리 페이지 (HR 전용)
 *
 * **역할:**
 * - 조직(OrgUnit) 목록 조회 및 표시
 * - 조직명 검색, 타입/사용여부 필터링
 * - Chapter 4-1: 조회 전용 (Read-Only) ✅
 * - Chapter 4-2: 조직 추가/수정/삭제 기능 구현 ✅
 * - V145: 조직장 개념 제거 - 조직장 UI/로직 완전 삭제
 * - V145.1: 승인자 기능 복구 (향후 근로/휴가 승인 워크플로우용)
 * - V26: CompanyCode 백필 임시 UI 추가 (system_admin 전용)
 *
 * **접근 권한:**
 * - HR (hr) 역할
 * - Super Admin (super_admin) 역할
 *
 * **Route Guard:**
 * - App.tsx에서 ProtectedRoute로 감싸져 있음
 * - 권한 없는 사용자는 ForbiddenPage로 이동
 *
 * **사용 예시 (App.tsx):**
 * ```tsx
 * <Route
 *   path="/hr/org-units"
 *   element={
 *     <ProtectedRoute path="/hr/org-units">
 *       <HrOrgUnitsPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */

import React, { useState } from 'react';
import {
  Search,
  Building2,
  Filter,
  Loader,
  AlertCircle,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
} from 'lucide-react';
import { useOrgUnits } from '../../shared/hooks/useOrgUnits';
import { useOrgUnitMutations } from '../../shared/hooks/useOrgUnitMutations';
// V154.1: useEmployees 제거 - getOrgUnitsForAdmin이 approverName까지 직접 반환
import { OrgUnitListFilter, OrgUnitType } from '../../shared/types/orgUnit';
import { OrgUnitFormModal } from './components/OrgUnitFormModal';

/**
 * 조직 타입 한글 표시명
 */
const ORG_TYPE_LABELS: Record<OrgUnitType, string> = {
  company: '회사',
  headquarter: '본부',
  division: '실',
  team: '팀',
  other: '기타',
};

/**
 * 조직 타입 선택 옵션
 */
const ORG_TYPE_OPTIONS: Array<{ value: OrgUnitType | ''; label: string }> = [
  { value: '', label: '전체' },
  { value: 'company', label: '회사' },
  { value: 'headquarter', label: '본부' },
  { value: 'division', label: '실' },
  { value: 'team', label: '팀' },
  { value: 'other', label: '기타' },
];

/**
 * 사용 여부 선택 옵션
 */
const STATUS_OPTIONS: Array<{ value: boolean | ''; label: string }> = [
  { value: '', label: '전체' },
  { value: true, label: '사용' },
  { value: false, label: '미사용' },
];

/**
 * HrOrgUnitsPage 컴포넌트
 */
export function HrOrgUnitsPage() {
  // ============================================
  // 상태 관리
  // ============================================

  // 검색/필터 상태
  const [searchName, setSearchName] = useState<string>('');
  const [selectedType, setSelectedType] = useState<OrgUnitType | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<boolean | ''>('');

  // 필터 객체 생성
  const filters: OrgUnitListFilter = {
    name: searchName || undefined,
    type: selectedType || undefined,
    isActive: selectedStatus === '' ? undefined : selectedStatus,
  };

  // 조직 목록 조회
  // V154.1: getOrgUnitsForAdmin이 approverName까지 직접 반환하므로 별도 직원 조회 불필요
  const { orgUnits, isLoading, error, refetch } = useOrgUnits(filters);

  // 선택된 조직 ID
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState<string | null>(null);

  // 모달 상태 (Chapter 4-2)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingOrgUnitId, setEditingOrgUnitId] = useState<string | undefined>(undefined);

  // Mutations (Chapter 4-2)
  const { deactivateOrgUnit, activateOrgUnit, deleteOrgUnitHard, isSubmitting } =
    useOrgUnitMutations();

  // ============================================
  // 이벤트 핸들러
  // ============================================

  /**
   * 조직 행 클릭 핸들러
   */
  const handleRowClick = (orgUnitId: string) => {
    setSelectedOrgUnitId(orgUnitId);
  };

  /**
   * 필터 초기화 핸들러
   */
  const handleResetFilters = () => {
    setSearchName('');
    setSelectedType('');
    setSelectedStatus('');
  };

  /**
   * 조직 추가 버튼 핸들러 (Chapter 4-2)
   */
  const handleAdd = () => {
    setEditingOrgUnitId(undefined);
    setIsModalOpen(true);
  };

  /**
   * 조직 수정 버튼 핸들러 (Chapter 4-2)
   */
  const handleEdit = (orgUnitId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지
    setEditingOrgUnitId(orgUnitId);
    setIsModalOpen(true);
  };

  /**
   * 조직 활성화/비활성화 토글 핸들러 (Chapter 4-2)
   */
  const handleToggleActive = async (orgUnit: typeof orgUnits[0], e: React.MouseEvent) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지

    const isActive = orgUnit.status === 'active';

    if (isActive) {
      // 비활성화
      if (
        !window.confirm(
          '이 조직을 비활성화하시겠습니까?\n하위 조직이 있으면 비활성화할 수 없습니다.'
        )
      ) {
        return;
      }

      try {
        await deactivateOrgUnit(orgUnit.id);
        await refetch(); // 목록 새로고침
        alert('조직이 비활성화되었습니다.');
      } catch (err) {
        alert(err instanceof Error ? err.message : '조직 비활성화에 실패했습니다.');
      }
    } else {
      // 활성화 (조건 검증 없음 - 옵션 A)
      if (!window.confirm('이 조직을 활성화하시겠습니까?')) {
        return;
      }

      try {
        await activateOrgUnit(orgUnit.id);
        await refetch(); // 목록 새로고침
        alert('조직이 활성화되었습니다.');
      } catch (err) {
        alert(err instanceof Error ? err.message : '조직 활성화에 실패했습니다.');
      }
    }
  };

  /**
   * 조직 완전 삭제 핸들러 (Hard Delete) (Chapter 4-2)
   */
  const handleDeleteHard = async (orgUnitId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지

    if (
      !window.confirm(
        '⚠️ 경고: 이 조직을 완전히 삭제하시겠습니까?\n\n' +
          '삭제 조건:\n' +
          '- 하위 조직이 없어야 합니다\n' +
          '- 소속 직원이 없어야 합니다\n' +
          '- 조직장/승인자로 지정되지 않아야 합니다\n\n' +
          '⚠️ 이 작업은 되돌릴 수 없습니다!'
      )
    ) {
      return;
    }

    try {
      await deleteOrgUnitHard(orgUnitId); // 완전 삭제
      await refetch(); // 목록 새로고침
      alert('조직이 완전히 삭제되었습니다.');
    } catch (err) {
      alert(err instanceof Error ? err.message : '조직 삭제에 실패했습니다.');
    }
  };

  /**
   * 모달 성공 콜백 (Chapter 4-2)
   */
  const handleModalSuccess = async () => {
    setIsModalOpen(false);
    setEditingOrgUnitId(undefined);
    await refetch(); // 목록 새로고침
  };

  // ============================================
  // 렌더링
  // ============================================

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* 페이지 헤더 */}
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: 'var(--color-gray-800)',
              marginBottom: '8px',
            }}
          >
            조직 관리
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>
            회사의 조직 구조를 관리합니다. (본부/실/팀)
          </p>
        </div>
        {/* 조직 추가 버튼 (Chapter 4-2) */}
        <button
          onClick={handleAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Plus size={20} />
          조직 추가
        </button>
      </div>

      {/* 검색/필터 영역 */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          {/* 조직명 검색 */}
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--color-gray-700)',
                marginBottom: '6px',
              }}
            >
              조직명 검색
            </label>
            <div style={{ position: 'relative' }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-gray-400)',
                }}
              />
              <input
                type="text"
                placeholder="조직명 입력..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>
          </div>

          {/* 조직 타입 필터 */}
          <div style={{ minWidth: '140px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--color-gray-700)',
                marginBottom: '6px',
              }}
            >
              조직 유형
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as OrgUnitType | '')}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'white',
              }}
            >
              {ORG_TYPE_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 사용 여부 필터 */}
          <div style={{ minWidth: '140px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--color-gray-700)',
                marginBottom: '6px',
              }}
            >
              사용 여부
            </label>
            <select
              value={selectedStatus === '' ? '' : String(selectedStatus)}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedStatus(value === '' ? '' : value === 'true');
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'white',
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option
                  key={option.value === '' ? 'all' : String(option.value)}
                  value={option.value === '' ? '' : String(option.value)}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 필터 초기화 버튼 */}
          <button
            onClick={handleResetFilters}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 16px',
              background: 'var(--color-gray-100)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--color-gray-700)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-gray-200)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-gray-100)';
            }}
          >
            <Filter size={16} />
            초기화
          </button>
        </div>
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            gap: '16px',
          }}
        >
          <Loader
            size={48}
            style={{
              color: 'var(--color-primary)',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ fontSize: '16px', color: 'var(--color-gray-600)' }}>
            조직 목록을 불러오는 중...
          </p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && (
        <div
          style={{
            background: 'var(--color-error-light, #FEE2E2)',
            border: '1px solid var(--color-error, #EF4444)',
            borderRadius: '12px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <AlertCircle size={48} style={{ color: 'var(--color-error)' }} />
          <div style={{ textAlign: 'center' }}>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--color-gray-800)',
                marginBottom: '8px',
              }}
            >
              조직 목록을 불러오는 중 오류가 발생했습니다.
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>
              {error.message}
            </p>
          </div>
          <button
            onClick={refetch}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'var(--color-error)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} />
            다시 시도
          </button>
        </div>
      )}

      {/* 조직 목록 테이블 */}
      {!isLoading && !error && (
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {/* 테이블 헤더 (목록 개수) */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--color-gray-200)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>
              조직 목록 ({orgUnits.length}개)
            </h3>
          </div>

          {/* 데이터 없음 */}
          {orgUnits.length === 0 && (
            <div
              style={{
                padding: '60px 20px',
                textAlign: 'center',
                color: 'var(--color-gray-500)',
              }}
            >
              <Building2 size={48} style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '500' }}>
                조직 데이터가 없습니다.
              </p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>
                상단의 '조직 추가' 버튼을 눌러 새로운 조직을 추가하세요.
              </p>
            </div>
          )}

          {/* 테이블 */}
          {orgUnits.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-gray-50)' }}>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      조직명
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      조직 코드
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      조직 유형
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      레벨
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      사용 여부
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      정렬 순서
                    </th>
                    {/* V145.1: 승인자 컬럼 복구 (조직장은 계속 제거 상태) */}
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                      }}
                    >
                      승인자
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                        width: '280px',
                        minWidth: '280px',
                      }}
                    >
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orgUnits.map((orgUnit) => (
                    <tr
                      key={orgUnit.id}
                      onClick={() => handleRowClick(orgUnit.id)}
                      style={{
                        cursor: 'pointer',
                        background:
                          selectedOrgUnitId === orgUnit.id
                            ? 'var(--color-primary-light, #E0F2FE)'
                            : 'white',
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedOrgUnitId !== orgUnit.id) {
                          e.currentTarget.style.background = 'var(--color-gray-50)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedOrgUnitId !== orgUnit.id) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: 'var(--color-gray-800)',
                          borderBottom: '1px solid var(--color-gray-200)',
                          fontWeight: '500',
                        }}
                      >
                        {orgUnit.name}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: 'var(--color-gray-600)',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        {orgUnit.code || '-'}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '13px',
                          textAlign: 'center',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            background: 'var(--color-primary-light, #DBEAFE)',
                            color: 'var(--color-primary-dark, #1E40AF)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {ORG_TYPE_LABELS[orgUnit.type]}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: 'var(--color-gray-600)',
                          textAlign: 'center',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        {orgUnit.level}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '13px',
                          textAlign: 'center',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            background:
                              orgUnit.status === 'active'
                                ? 'var(--color-success-light, #D1FAE5)'
                                : 'var(--color-gray-200)',
                            color:
                              orgUnit.status === 'active'
                                ? 'var(--color-success-dark, #065F46)'
                                : 'var(--color-gray-600)',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {orgUnit.status === 'active' ? '사용' : '미사용'}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: 'var(--color-gray-600)',
                          textAlign: 'center',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        {orgUnit.order}
                      </td>
                      {/* V145.1: 승인자 컬럼 복구 */}
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '14px',
                          color: 'var(--color-gray-600)',
                          textAlign: 'center',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        {/* V154.1: CF에서 approverName 직접 반환 */}
                        {orgUnit.approverName || (orgUnit.approverEmployeeId ? `ID: ${orgUnit.approverEmployeeId}` : '-')}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          textAlign: 'center',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                            flexWrap: 'nowrap',
                          }}
                        >
                          <button
                            onClick={(e) => handleEdit(orgUnit.id, e)}
                            disabled={isSubmitting}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              background: 'var(--color-primary-light, #DBEAFE)',
                              color: 'var(--color-primary-dark, #1E40AF)',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.5 : 1,
                              transition: 'all 0.2s ease',
                              whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubmitting) {
                                e.currentTarget.style.background = 'var(--color-primary)';
                                e.currentTarget.style.color = 'white';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubmitting) {
                                e.currentTarget.style.background = 'var(--color-primary-light, #DBEAFE)';
                                e.currentTarget.style.color = 'var(--color-primary-dark, #1E40AF)';
                              }
                            }}
                          >
                            <Edit size={14} />
                            수정
                          </button>
                          <button
                            onClick={(e) => handleToggleActive(orgUnit, e)}
                            disabled={isSubmitting}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              background:
                                orgUnit.status === 'active'
                                  ? 'var(--color-warning-light, #FEF3C7)'
                                  : 'var(--color-success-light, #D1FAE5)',
                              color:
                                orgUnit.status === 'active'
                                  ? 'var(--color-warning-dark, #92400E)'
                                  : 'var(--color-success-dark, #065F46)',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.5 : 1,
                              transition: 'all 0.2s ease',
                              whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubmitting) {
                                if (orgUnit.status === 'active') {
                                  e.currentTarget.style.background = 'var(--color-warning, #F59E0B)';
                                  e.currentTarget.style.color = 'white';
                                } else {
                                  e.currentTarget.style.background = 'var(--color-success, #10B981)';
                                  e.currentTarget.style.color = 'white';
                                }
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubmitting) {
                                if (orgUnit.status === 'active') {
                                  e.currentTarget.style.background = 'var(--color-warning-light, #FEF3C7)';
                                  e.currentTarget.style.color = 'var(--color-warning-dark, #92400E)';
                                } else {
                                  e.currentTarget.style.background = 'var(--color-success-light, #D1FAE5)';
                                  e.currentTarget.style.color = 'var(--color-success-dark, #065F46)';
                                }
                              }
                            }}
                          >
                            {orgUnit.status === 'active' ? (
                              <>
                                <Trash2 size={14} />
                                비활성화
                              </>
                            ) : (
                              <>
                                <RefreshCw size={14} />
                                활성화
                              </>
                            )}
                          </button>
                          <button
                            onClick={(e) => handleDeleteHard(orgUnit.id, e)}
                            disabled={isSubmitting}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 12px',
                              background: 'var(--color-error-light, #FEE2E2)',
                              color: 'var(--color-error, #EF4444)',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.5 : 1,
                              transition: 'all 0.2s ease',
                              whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubmitting) {
                                e.currentTarget.style.background = 'var(--color-error, #EF4444)';
                                e.currentTarget.style.color = 'white';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubmitting) {
                                e.currentTarget.style.background = 'var(--color-error-light, #FEE2E2)';
                                e.currentTarget.style.color = 'var(--color-error, #EF4444)';
                              }
                            }}
                          >
                            <Trash2 size={14} />
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 조직 생성/수정 모달 (Chapter 4-2) */}
      <OrgUnitFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
        orgUnitId={editingOrgUnitId}
      />
    </div>
  );
}

/**
 * ============================================
 * Chapter 4-2 확장 계획
 * ============================================
 *
 * **1. 조직 추가 버튼**
 * - 상단 헤더에 "+ 조직 추가" 버튼 추가
 * - 클릭 시 모달 또는 사이드 패널로 조직 생성 폼 표시
 *
 * **2. 조직 상세 보기**
 * - 행 클릭 시 오른쪽 패널 또는 별도 페이지로 이동
 * - 조직 정보 수정/삭제 버튼 제공
 *
 * **3. 계층 구조 표시**
 * - 트리 뷰 형태로 조직도 표시
 * - 드래그앤드롭으로 조직 이동 (조직 개편)
 *
 * **4. 일괄 작업**
 * - 체크박스로 여러 조직 선택
 * - 일괄 상태 변경 (사용/미사용)
 * - 일괄 삭제
 *
 * **5. 엑셀 내보내기**
 * - 조직 목록을 Excel/CSV로 다운로드
 * - 조직 구조 템플릿 제공
 */

// Loader 애니메이션 스타일 추가
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);
